import { notifyError } from "common/utils/app-monitoring/notify";
import buildRequestFingerprint from "./build-request-fingerprint";
import { classifyNetworkSubType, shouldReportError } from "./error-classification";

// HB-1926: include request URL/method/status in every Honeybadger notice so we
// can identify which endpoint is failing. `responseSnippet` is truncated at 200
// chars; pass `responseBody` as a string (or undefined to skip the snippet).
// HB-1942: also pass a stable fingerprint as the top-level HB grouping field
// so the vendor-frame backtrace collapse doesn't merge async-fetch errors
// from different endpoints into one bucket.
export const reportRequestError = (error, { url, method, responseBody, silent }) => {
  if (silent) return;
  try {
    if (!shouldReportError(error)) return;
    const httpStatus = error.status ?? error.response?.status;
    const emptyBody = responseBody === "";
    if (httpStatus >= 500 && emptyBody && /\/api\/v2\/estimates\//.test(url || "")) return;

    const fingerprint = buildRequestFingerprint(method, url, httpStatus);
    notifyError(error, {
      context: {
        requestUrl: url,
        requestMethod: method,
        httpStatus,
        responseSnippet: typeof responseBody === "string" ? responseBody.slice(0, 200) : undefined
      },
      ...(fingerprint != null ? { fingerprint } : {})
    });
  } catch (err) {
    notifyError("Error has malformed data", {
      context: { requestUrl: url, requestMethod: method }
    });
  }
};

// MN-8803: build the "Unable to connect to server" sentinel while PRESERVING the
// original error as `.cause` and attaching a low-cardinality `.networkSubType`, so
// the previously-discarded provenance survives for whoever ultimately reports it.
// The message is kept byte-identical so isAxiosNetworkError's sentinel branch and
// downstream `=== "Unable to connect to server"` consumers still match.
//
// NOTE: we deliberately do NOT emit a Honeybadger notice here. No-status network
// errors are suppressed at the request layer (shouldReportError), and the 1,232-occ
// cluster is actually reported downstream when the sentinel ESCAPES uncaught — by the
// React-root error handlers (common/react/create-react-root.ts) and the checkout
// unhandledrejection listener (common/utils/checkout-observability). Reporting here
// too would DOUBLE-COUNT those, violating the "don't double-count infra blips" AC.
// Splitting the cluster by sub-type therefore belongs at those downstream reporters
// (they can read `.networkSubType` off the cause) — tracked as a follow-up.
export const buildNetworkConnectionError = error => {
  const networkError = new Error("Unable to connect to server");
  networkError.cause = error;
  networkError.networkSubType = classifyNetworkSubType(error);
  return networkError;
};
